import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import './Tooltip.scss'

// Simple accessible tooltip component.
// Usage:
// <Tooltip content={<div>Multi-line<br/>content</div>} placement="top"> <span>Trigger</span> </Tooltip>
// Focus and hover both reveal tooltip; ESC closes.
export const Tooltip = ({ content, children, placement = 'top', delay = 150, maxWidth = 240 }) => {
  const [visible, setVisible] = useState(false)
  const [style, setStyle] = useState({})
  const triggerRef = useRef(null)
  const tipRef = useRef(null)
  const openTimer = useRef(null)
  const closeTimer = useRef(null)

  const show = () => {
    window.clearTimeout(closeTimer.current)
    openTimer.current = window.setTimeout(() => setVisible(true), delay)
  }
  const hide = () => {
    window.clearTimeout(openTimer.current)
    closeTimer.current = window.setTimeout(() => setVisible(false), 50)
  }

  // Positioning after render
  useEffect(() => {
    if (visible && triggerRef.current && tipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const tip = tipRef.current
      const gap = 8
      let top = 0
      let left = 0
      switch (placement) {
        case 'bottom':
          top = rect.bottom + gap
          left = rect.left + rect.width / 2
          break
        case 'left':
          top = rect.top + rect.height / 2
          left = rect.left - gap
          break
        case 'right':
          top = rect.top + rect.height / 2
          left = rect.right + gap
          break
        case 'top':
        default:
          top = rect.top - gap
          left = rect.left + rect.width / 2
      }
      requestAnimationFrame(() => {
        const { offsetWidth, offsetHeight } = tip
        let finalLeft = left - offsetWidth / 2
        let finalTop = top - offsetHeight
        if (placement === 'bottom') finalTop = top
        if (placement === 'left') finalLeft = left - offsetWidth
        if (placement === 'right') finalLeft = left
        setStyle({ top: `${finalTop}px`, left: `${finalLeft}px`, maxWidth })
      })
    }
  }, [visible, placement, maxWidth])

  useEffect(() => () => { window.clearTimeout(openTimer.current); window.clearTimeout(closeTimer.current) }, [])

  const onKeyDown = e => { if (e.key === 'Escape') setVisible(false) }

  return (
    <span className="tooltip-wrapper" ref={triggerRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      {children}
      {visible && (
        <div className={`tooltip-bubble placement-${placement}`} style={style} ref={tipRef} role="tooltip">
          <div className="tooltip-content">{content}</div>
          <span className="tooltip-arrow" />
        </div>
      )}
    </span>
  )
}

Tooltip.propTypes = {
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  delay: PropTypes.number,
  maxWidth: PropTypes.number
}

export default Tooltip
